import * as three from "three";
import RectangleMesh from "../../instancing/Meshes/RectangleMesh";
import RectangleOutlineMesh from "../../instancing/Meshes/RectOutlineMesh";
import getMaterialWithUniforms from "../../instancing/materials/getMaterialWithUniforms";

const barHeight = 4;
const confLineWidth = 1;

export function getDubCallMesh() {
  const conf = { posX: 1, rgbaCode: 1, posY: 1 };
  const colorMap = Array(5).fill(new three.Vector4(0.4, 0.2, 0.7, 0.2));
  const materialConf = getMaterialWithUniforms({})
    .setColorMap(colorMap)
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-barHeight, -barHeight, barHeight, barHeight])
    .setOffset([0, 0]);

  return new RectangleMesh(materialConf, conf);
}

export function getDellCallMesh() {
  const colorMap = Array(5).fill(new three.Vector4(0.4, 0.2, 0.7, 0.2));

  const conf = {
    rgbaCode: 1,
    raycastInd: 1,
    posX: 1,
    posY: 1,
  };

  const materialConf = getMaterialWithUniforms({})
    .setPaddingX([-1, 1, -1, 1])
    .setPaddingY([-barHeight, -barHeight, barHeight, barHeight])
    .setOffset([0, 0])
    .setBorderWidth(2)
    .setRaycastBorderWidth(9)
    .setColorMap(colorMap);

  return new RectangleOutlineMesh(materialConf, conf);
}

export function getCiPosEndBarMesh() {
  const h = confLineWidth / 2;
  const conf = { posX: 1, rgbaCode: 1, posY: 1 };
  const colorMap = Array(1).fill(new three.Vector4(0.6, 0.1, 0.3, 0.1));
  const matConf = {
    disableRaycast: true,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  };
  const materialConf = getMaterialWithUniforms(matConf)
    .setColorMap(colorMap)
    .setPaddingX([1, -1, 1, -1])
    .setPaddingY([-h, -h, h, h])
    .setOffset([0, 0]);

  return new RectangleMesh(materialConf, conf);
}

export function getCiPosEndVertMesh() {
  const h = 3;
  const conf = { posX: 1, rgbaCode: 1, posY: 1 };
  const colorMap = Array(1).fill(new three.Vector4(0.6, 0.1, 0.3, 0.1));
  const matConf = {
    disableRaycast: true,
    transparent: true,
    depthWrite: false,
    depthTest: false,
  };
  const materialConf = getMaterialWithUniforms(matConf)
    .setColorMap(colorMap)
    .setPaddingX([-confLineWidth, confLineWidth, -confLineWidth, confLineWidth])
    .setPaddingY([-h, -h, h, h])
    .setOffset([0, 0]);

  return new RectangleMesh(materialConf, conf);
}

export function drawCall(calls, mesh) {
  const count = calls.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const { region, pathoIndex } = calls[i];
    const { g0, g1 } = region;
    setX(i, g0, g1, g0, g1);
    setRgb(i, pathoIndex, pathoIndex, pathoIndex, pathoIndex);
  }
}

export function updateCall(items, mesh, filters) {
  const zygoFils = new Set(filters.filter((x) => x.indexOf("zygo") === 0));
  const pathFils = new Set(filters.filter((x) => x.indexOf("path") === 0));
  const precFils = new Set(filters.filter((x) => x.indexOf("prec") === 0));
  const typeFils = new Set(filters.filter((x) => x.indexOf("type") === 0));

  const setY = mesh.updateY({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const { readratio, zygosityIndex, pathoIndex, precisionIndex, typeIndex } =
      items[i];

    if (filters && filters.length) {
      const zygoKey = `zygo-${zygosityIndex}`;
      const pathKey = `path-${pathoIndex}`;
      const precKey = `prec-${precisionIndex}`;
      const typeKey = `type-${typeIndex}`;

      const hasZygo = zygoFils.size === 0 || zygoFils.has(zygoKey);
      const hasPrec = precFils.size === 0 || precFils.has(precKey);
      const hasPath = pathFils.size === 0 || pathFils.has(pathKey);
      const hasType = typeFils.size === 0 || typeFils.has(typeKey);
      const isVisible = hasZygo && hasPrec && hasPath && hasType;

      const y = isVisible ? readratio : -5000;
      setY(i, y, y, y, y);
    } else {
      const y = readratio;
      setY(i, y, y, y, y);
    }
  }
}

export function updateCallConf(items, mesh, filters) {
  const zygoFils = new Set(filters.filter((x) => x.indexOf("zygo") === 0));
  const pathFils = new Set(filters.filter((x) => x.indexOf("path") === 0));
  const precFils = new Set(filters.filter((x) => x.indexOf("prec") === 0));
  const typeFils = new Set(filters.filter((x) => x.indexOf("type") === 0));

  const setY = mesh.updateY({ count: items.length, isAppend: false });
  for (let i = 0; i < items.length; i++) {
    const { readratio, zygosityIndex, pathoIndex, precisionIndex, typeIndex } =
      items[i].call;

    if (filters && filters.length) {
      const zygoKey = `zygo-${zygosityIndex}`;
      const pathKey = `path-${pathoIndex}`;
      const precKey = `prec-${precisionIndex}`;
      const typeKey = `type-${typeIndex}`;

      const hasZygo = zygoFils.size === 0 || zygoFils.has(zygoKey);
      const hasPrec = precFils.size === 0 || precFils.has(precKey);
      const hasPath = pathFils.size === 0 || pathFils.has(pathKey);
      const hasType = typeFils.size === 0 || typeFils.has(typeKey);
      const isVisible = hasZygo && hasPrec && hasPath && hasType;
      const y = isVisible ? readratio : -5000;
      setY(i, y, y, y, y);
    } else {
      const y = readratio;
      setY(i, y, y, y, y);
    }
  }
}

export function drawVert(reads, mesh, isStart) {
  const count = reads.length;
  const isAppend = true;
  const setX = mesh.updateX({ count, isAppend });
  const setRgb = mesh.updateRgba({ count, isAppend });
  for (let i = 0; i < count; i++) {
    const { region, isOut } = reads[i];
    const { g0, g1 } = region;
    const x = isStart ? g0 : g1;
    const rgb = isOut ? 4 : 0;
    setX(i, x, x, x, x);
    setRgb(i, rgb, rgb, rgb, rgb);
  }
}
